import { ReactElement, useState } from 'react';

import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconEquals } from '@components/icons/equals';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconTraveller } from '@components/icons/traveller';
import { IconTravellerStatusInProgress } from '@components/icons/traveller-status-in-progress';
import { IconTravellerStatusNotStarted } from '@components/icons/traveller-status-not-started';
import { IconTravellerStatusPaused } from '@components/icons/traveller-status-paused';
import { InfoBox } from '@components/info-box';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Modal, hideModal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { ShowMore } from '@components/show-more';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  useBulkCompletePreview,
  useBulkCompleteTravellerJourneys,
} from 'api/traveller-journeys/hooks';
import { TravellerJourneyStatus } from 'api/types';

import { statusLabelMap, statusTagMap } from '../journeys/traveller-journey-list/constants';

export type MarkCompletedTraveller = {
  shortId: string;
  name: string;
  picture?: string;
  status: TravellerJourneyStatus;
};

const getEligibleTravellers = (travellers: MarkCompletedTraveller[]) => {
  return travellers.filter((traveller) => traveller.status !== 'completed');
};

const TRAVELLERS_PER_STEP = 50;

export const useMarkCompletedModal = () => useModal(MarkCompletedModal);

type MarkCompletedModalProps = {
  journeyId: string;
  travellers: MarkCompletedTraveller[];
  onSuccess?: () => void;
};

export const MarkCompletedModal = ({
  journeyId,
  travellers,
  onSuccess,
}: MarkCompletedModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [reason, setReason] = useState('');
  const completeMutation = useBulkCompleteTravellerJourneys(journeyId, {
    onSuccess: () => {
      hideModal();
      onSuccess?.();
    },
  });
  const eligibleIds = getEligibleTravellers(travellers).map((traveller) => traveller.shortId);

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} icon={<IconCheckmark color="elementBlue100" />} />
      <Modal.Content>
        <MarkCompletedDetails
          journeyId={journeyId}
          travellers={travellers}
          onReasonChange={setReason}
        />
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
          {eligibleIds.length > 0 && (
            <Button
              variant="primary-blue"
              onClick={() => completeMutation.mutate({ ids: eligibleIds, reason })}
              disabled={completeMutation.isLoading}
              iconLeft={<IconCheckmark />}
              text={t('confirm')}
              dataTestid="confirm-modal-primary-button"
            />
          )}
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

type MarkCompletedDetailsProps = {
  journeyId: string;
  travellers: MarkCompletedTraveller[];
  onReasonChange: (reason: string) => void;
};

export const MarkCompletedDetails = ({
  journeyId,
  travellers,
  onReasonChange,
}: MarkCompletedDetailsProps) => {
  const { t } = useScopedTranslate(locale);
  const eligibleTravellers = getEligibleTravellers(travellers);
  const alreadyCompletedCount = travellers.length - eligibleTravellers.length;
  const previewQuery = useBulkCompletePreview(
    journeyId,
    eligibleTravellers.map((traveller) => traveller.shortId),
  );
  const actionsToSkipById = Object.fromEntries(
    (previewQuery.data ?? []).map((item) => [item.shortId, item.actionsToSkip]),
  );

  return (
    <Stack space={6}>
      <Stack space={2}>
        <SummaryRow
          icon={<IconTraveller color="elementPurple50" />}
          text={t('travellersSelected')}
          count={travellers.length}
        />
        {alreadyCompletedCount > 0 && (
          <Text
            font="regular14"
            color="textSecondary"
            text={t('alreadyCompleted', { count: alreadyCompletedCount })}
          />
        )}
        <SummaryRow
          icon={<IconEquals color="elementPurple50" />}
          text={t('travellersToUpdate')}
          count={eligibleTravellers.length}
        />
      </Stack>
      {eligibleTravellers.length > 0 && (
        <Stack space={2}>
          <Stack orientation="horizontal" justify="flex-end">
            <Text font="regular12" color="textHelper" text={t('actionsToSkip')} />
          </Stack>
          <TravellerList data-testid="mark-completed-traveller-list">
            <ShowMore
              items={eligibleTravellers}
              increment={TRAVELLERS_PER_STEP}
              renderItem={(traveller) => {
                const actionsToSkip = actionsToSkipById[traveller.shortId];
                return (
                  <TravellerRow key={traveller.shortId}>
                    <TravellerName>
                      <Avatar name={traveller.name} src={traveller.picture} size="sm" />
                      <Text
                        font="regular14"
                        color="textPrimary"
                        text={traveller.name}
                        ellipsisWithTooltip
                      />
                    </TravellerName>
                    <TravellerDetails>
                      <Badge
                        size="md"
                        variant={
                          traveller.status === 'paused' ? 'purple' : statusTagMap[traveller.status]
                        }
                        icon={statusIconMap[traveller.status]}
                        text={statusLabelMap[traveller.status]}
                      />
                      {previewQuery.isLoading && (
                        <SizedRelativeSpinner size={16} $skipMargin $borderWidth={1.5} />
                      )}
                      {actionsToSkip !== undefined && (
                        <Badge size="md" variant="neutral" text={actionsToSkip} />
                      )}
                    </TravellerDetails>
                  </TravellerRow>
                );
              }}
            />
          </TravellerList>
          {previewQuery.isError && (
            <Text font="regular12" color="textNegative" text={t('previewFailed')} />
          )}
        </Stack>
      )}
      <InfoBox variant="orange" icon={<IconFormIndicatorWarning />} text={t('warning')} />
      <Separator />
      <TextArea
        label={t('reasonLabel')}
        labelIconAfter={<Text font="regular14" color="textHelper" text={t('reasonOptional')} />}
        placeholder={t('reasonPlaceholder')}
        maxLength={255}
        style={{ minHeight: pxToRem(64) }}
        onChange={onReasonChange}
      />
    </Stack>
  );
};

const statusIconMap: Partial<Record<TravellerJourneyStatus, ReactElement<IconProps>>> = {
  not_started: <IconTravellerStatusNotStarted color="elementPurple50" />,
  in_progress: <IconTravellerStatusInProgress />,
  paused: <IconTravellerStatusPaused color="elementPurple80" />,
};

const SummaryRow = ({
  icon,
  text,
  count,
}: {
  icon: ReactElement<IconProps>;
  text: string;
  count: number;
}) => {
  return (
    <Stack orientation="horizontal" align="center" justify="space-between">
      <Stack orientation="horizontal" align="center" space={2}>
        {icon}
        <Text font="semibold14" text={text} />
      </Stack>
      <Badge size="md" variant="neutral" text={count} />
    </Stack>
  );
};

const TravellerList = styled.div({
  maxHeight: pxToRem(200),
  overflowY: 'auto',
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
});

const TravellerRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.space[2],
  padding: theme.space[2],
  '&:not(:last-child)': {
    borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
  },
});

const TravellerName = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.space[2],
  flex: 1,
  minWidth: 0,
});

const TravellerDetails = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.space[2],
  flexShrink: 0,
});

const locale = {
  en: {
    title: 'Mark as completed',
    travellersSelected: 'Traveller/s Selected',
    alreadyCompleted: '{count} selected traveller are already completed',
    travellersToUpdate: 'Traveller/s that will be updated',
    actionsToSkip: 'Actions that will be skipped',
    warning:
      'Remaining actions are skipped for these travellers. No emails, forms or app actions will run',
    reasonLabel: 'Reason',
    reasonOptional: '(Optional)',
    reasonPlaceholder: "Shown in each traveller's activity...",
    confirm: 'Mark as completed',
    previewFailed: 'Could not load how many actions will be skipped.',
  },
};
